/**
 * Marks what the server refused as unavailable in the menu the customer already holds.
 *
 * When /clientTransaction refuses an order as items_unavailable, the menu on screen is
 * still the getDataAuthStore snapshot from when the app opened, so the refused product —
 * or a package containing it — stays orderable, and the customer can add it straight back
 * and be refused again. Re-downloading getDataAuthStore would fix that but it carries
 * every product's configuration and can exceed 5 MB, so instead the refusal is applied
 * to the snapshot we have.
 *
 * Pure: takes the getDataAuthStore `product` tree (categories, possibly nested, holding
 * products) and returns a new tree, leaving the input untouched. Unchanged branches are
 * returned as-is, so a refusal touching two products does not copy the whole menu.
 *
 * What becomes unavailable (is_active: false — what every tile draws as sold out, and what
 * the order modal tests to decide whether an option can be chosen):
 *   - any product, variant, option or bundled product whose plu_number was refused;
 *   - a product the refusal leaves impossible to order. In RDO's data a package is a
 *     product with choice groups (prep_group, each with choose {min, max}) rather than a
 *     sub_product bundle, so that means a REQUIRED group (choose.min >= 1) left with no
 *     available option — e.g. "PACKAGE SMIRNOFF", whose own group offers only #SMIRNOFF.
 *     One sold-out option among alternatives only disables that option: the package is
 *     still orderable with another mixer, and hiding it would turn away a valid order.
 *     The same goes for a variant product with no variant left, and for a sub_product
 *     bundle with any item gone (a bundle is fixed, so there is no alternative).
 * Each rule only looks at what this refusal changed, so a product that was already in an
 * odd state before is left exactly as the menu delivered it.
 */

const isUnavailable = (node) => node?.is_active === false;

/** Returns the node, or a patched copy when it or anything below it changed. */
function patchProduct(node, refused, touched) {
  if (!node || typeof node !== 'object') return node;

  let next = node;
  const replace = (key, value) => {
    if (value !== node[key]) {
      if (next === node) next = { ...node };
      next[key] = value;
    }
  };

  const patchList = (list) => {
    if (!Array.isArray(list)) return list;
    let changed = false;
    const out = list.map((child) => {
      const patched = patchProduct(child, refused, touched);
      if (patched !== child) changed = true;
      return patched;
    });
    return changed ? out : list;
  };

  const bundle = patchList(node.sub_product);
  const variants = patchList(node.variant_item);
  replace('sub_product', bundle);
  replace('variant_item', variants);

  let requiredGroupEmptied = false;
  if (Array.isArray(node.prep_group)) {
    let changed = false;
    const groups = node.prep_group.map((group) => {
      const items = patchList(group?.prep_item);
      if (items === group?.prep_item) return group;
      changed = true;
      const required = Number(group?.choose?.min ?? 0) >= 1;
      if (required && items.length > 0 && items.every(isUnavailable)) {
        requiredGroupEmptied = true;
      }
      return { ...group, prep_item: items };
    });
    if (changed) replace('prep_group', groups);
  }

  const bundleChanged = bundle !== node.sub_product;
  const variantsChanged = variants !== node.variant_item;
  const unavailable =
    (node.plu_number && refused.has(node.plu_number)) ||
    requiredGroupEmptied ||
    (bundleChanged && bundle.some(isUnavailable)) ||
    (variantsChanged && variants.length > 0 && variants.every(isUnavailable));

  if (unavailable && next.is_active !== false) {
    if (next === node) next = { ...node };
    next.is_active = false;
  }
  if (unavailable && next.uuid_product) touched.add(next.uuid_product);

  return next;
}

function patchCategory(category, refused, touched) {
  if (!category || typeof category !== 'object') return category;
  let next = category;

  for (const key of ['categories', 'products']) {
    const list = category[key];
    if (!Array.isArray(list)) continue;
    let changed = false;
    const out = list.map((child) => {
      const patched =
        key === 'categories'
          ? patchCategory(child, refused, touched)
          : patchProduct(child, refused, touched);
      if (patched !== child) changed = true;
      return patched;
    });
    if (changed) {
      if (next === category) next = { ...category };
      next[key] = out;
    }
  }
  return next;
}

/**
 * @param categories the getDataAuthStore `product` tree
 * @param refusedPlus Set (or array) of plu_numbers the server refused
 * @returns {{ categories: Array, uuids: Set<string> }} the patched tree, and the
 *   uuid_product of everything now unavailable — tiles also consult the sell-time store
 *   by uuid, so those entries need the same flag.
 */
export function markUnavailable(categories, refusedPlus) {
  const refused = refusedPlus instanceof Set ? refusedPlus : new Set(refusedPlus ?? []);
  const touched = new Set();
  if (!Array.isArray(categories) || refused.size === 0) {
    return { categories, uuids: touched };
  }
  let changed = false;
  const out = categories.map((category) => {
    const patched = patchCategory(category, refused, touched);
    if (patched !== category) changed = true;
    return patched;
  });
  return { categories: changed ? out : categories, uuids: touched };
}
